import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import UpcomingAppointmentsCard from '../../../components/upcomingAppointmentsCard'
import { Section, SectionTitle, SectionHeader } from '../../../styled/sections'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import Feedback from '../../../components/feedback'
import Tabs from '../../../components/ui/tabs'
import AttendanceCard from '../../../components/attendanceCard'
import { useUpcomingAppointments } from '../../../hooks/useUpcomingAppointments'
import { usePastAppointments } from '../../../hooks/usePastAppointments'
import { Button } from '../../../components/ui/button'
import { Filter } from '../../../assets/icons'
import ModalFilter from '../../../components/Modal/ModalFilter'
import Pagination from '../../../components/pagination'
import { usePaginate } from '../../../hooks/useFilteredLabResults'
import { addFlashMessage } from '../../../actions'
import { capitalizeEachWord } from '../../../utils/stringsUtils'

const UPCOMING_TAB = { id: 0, name: 'Próximos' }
const HISTORY_TAB = { id: 1, name: 'Históricos' }

const HISTORY_ITEMS_PER_PAGE = 9

const UpcomingAppointments = props => {
	const history = useHistory()
	const location = useLocation()
	const [showFeedback, setShowFeedback] = useState({ show: false, success: false })
	const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0)
	const [showFiltersModal, setShowFiltersModal] = useState(false)
	const [filters, setFilters] = useState({})
	const [historyPage, setHistoryPage] = useState(1)

	const handleTabChange = tab => {
		setActiveTab(tab)
		history.replace({ pathname: location.pathname, state: { ...location.state, activeTab: tab } })
	}
	const {
		todayAppointments,
		allUpcomingAppointments,
		isLoading: isLoadingUpcoming
	} = useUpcomingAppointments({
		patientId: props.patient?.idPaciente,
		addFlashMessage: props.addFlashMessage,
		limit: Number.MAX_SAFE_INTEGER
	})

	const {
		pastAppointments,
		attendanceRate,
		clinicsOptions,
		isLoading: isLoadingPast
	} = usePastAppointments({
		nroDocumento: props.patient?.numeroDocumento,
		addFlashMessage: props.addFlashMessage,
		filters
	})

	const professionalOptions = useMemo(() => {
		const names = [...todayAppointments, ...allUpcomingAppointments, ...pastAppointments]
			.map(appointment => appointment.nombreProfesional)
			.filter(Boolean)
		return [...new Set(names)]
			.sort()
			.map(name => ({ value: name, label: capitalizeEachWord(name) }))
	}, [todayAppointments, allUpcomingAppointments, pastAppointments])

	const filterFields = useMemo(
		() => [
			{
				type: 'daterange',
				name: 'fecha',
				startField: 'fechaDesde',
				endField: 'fechaHasta',
				label: 'Filtrar por fecha',
				placeholder: 'Ingresá un rango de fechas'
			},
			{
				type: 'select',
				name: 'nombreProfesional',
				label: 'Filtrar por profesional',
				placeholder: 'Seleccioná un profesional',
				options: professionalOptions,
				isClearable: true
			},
			{
				type: 'select',
				name: 'idClinica',
				label: 'Filtrar por clínica',
				placeholder: 'Seleccioná una clínica',
				options: clinicsOptions,
				isClearable: true
			}
		],
		[professionalOptions, clinicsOptions]
	)

	const filteredPastAppointments = useMemo(
		() => [...pastAppointments].sort((a, b) => new Date(b.fechaAgenda) - new Date(a.fechaAgenda)),
		[pastAppointments]
	)

	useEffect(() => {
		setHistoryPage(1)
	}, [filteredPastAppointments])

	const {
		paginatedResults: paginatedPastAppointments,
		totalPages: historyTotalPages,
		page: currentHistoryPage
	} = usePaginate(filteredPastAppointments, historyPage, HISTORY_ITEMS_PER_PAGE)

	const filtersCount = [
		filters.nombreProfesional,
		filters.idClinica,
		filters.fechaDesde || filters.fechaHasta
	].filter(Boolean).length

	const hasUpcoming = todayAppointments.length > 0 || allUpcomingAppointments.length > 0
	const hasHistory = pastAppointments.length > 0 || filtersCount > 0
	const tabs = [...(hasUpcoming ? [UPCOMING_TAB] : []), ...(hasHistory ? [HISTORY_TAB] : [])]

	useEffect(() => {
		if (isLoadingUpcoming || isLoadingPast) return
		if (!hasUpcoming && activeTab === UPCOMING_TAB.id) {
			handleTabChange(HISTORY_TAB.id)
		} else if (!hasHistory && activeTab === HISTORY_TAB.id) {
			handleTabChange(UPCOMING_TAB.id)
		}
	}, [hasUpcoming, hasHistory, activeTab, isLoadingUpcoming, isLoadingPast])

	return (
		<div style={{ width: '100%' }}>
			{showFeedback.show ? (
				<Feedback
					title={
						showFeedback.success ? 'Tu turno fue cancelado con éxito' : 'Error al cancelar el turno'
					}
					bodyText={
						showFeedback.success
							? undefined
							: 'Hubo un error al cancelar el turno. Por favor, inténtelo de nuevo.'
					}
					buttonLabel={'Volver al inicio'}
					onClickButton={() => history.push('/inicio')}
					isSuccess={showFeedback.success}
					isError={!showFeedback.success}
				/>
			) : (
				<Section>
					<SectionHeader align="left">
						<SectionTitle>Mis turnos</SectionTitle>
					</SectionHeader>
					{attendanceRate.total > 0 && (
						<AttendanceCard
							attended={attendanceRate.attended}
							total={attendanceRate.total}
							percentage={attendanceRate.percentage}
						/>
					)}
					<Section gap="1.5vw">
						<Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
						{activeTab === 0 ? null : (
							<Button
								$variant="cardNav"
								iconLeft={<Filter />}
								label={`Filtros ${filtersCount > 0 ? `(${filtersCount})` : ''}`}
								width="207px"
								onClick={() => setShowFiltersModal(true)}
							/>
						)}

						{activeTab === 0 ? (
							<>
								{todayAppointments.length > 0 && (
									<Section gap="0.75vw" mb="0px">
										<SectionTitle size="18px" sizeMobile="16px" mbDesktop="0px" mb="0px">
											Turnos para hoy
										</SectionTitle>
										<UpcomingAppointmentsCard
											appointments={todayAppointments}
											setShowFeedback={setShowFeedback}
										/>
									</Section>
								)}
								{allUpcomingAppointments.length > 0 && (
									<Section gap="0.75vw" mb="0px">
										<SectionTitle size="18px" sizeMobile="16px" mbDesktop="0px" mb="0px">
											Próximos turnos
										</SectionTitle>
										<UpcomingAppointmentsCard
											appointments={allUpcomingAppointments}
											setShowFeedback={setShowFeedback}
										/>
									</Section>
								)}
							</>
						) : (
							<>
								<UpcomingAppointmentsCard
									appointments={paginatedPastAppointments}
									isHistory
									sortDirection="desc"
								/>
								{filteredPastAppointments.length > 0 && (
									<Pagination
										totalPages={historyTotalPages}
										currentPage={currentHistoryPage}
										onPageChange={setHistoryPage}
									/>
								)}
							</>
						)}
					</Section>
				</Section>
			)}
			<ModalFilter
				showModal={showFiltersModal}
				onClose={() => setShowFiltersModal(false)}
				initialFilters={filters}
				filterFields={filterFields}
				showSort={false}
				showTitleMobile={true}
				stickyButtonsMobile={true}
				center={false}
				onApplyFilters={({ nombreProfesional, idClinica, fechaDesde, fechaHasta }) => {
					setFilters({
						nombreProfesional: nombreProfesional || null,
						idClinica: idClinica || null,
						fechaDesde: fechaDesde || null,
						fechaHasta: fechaHasta || null
					})
					setShowFiltersModal(false)
				}}
			/>
		</div>
	)
}

const mapStateToProps = ({ patient }) => ({ patient })

const mapDispatchToProps = {
	addFlashMessage
}

export default connect(mapStateToProps, mapDispatchToProps)(UpcomingAppointments)
